import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { PageHeader, ToolTiles, Section } from "@/components/dashboard/DashboardPrimitives";
import ReportsPanel from "@/components/dashboard/ReportsPanel";
import ParentQuizCard from "@/components/dashboard/ParentQuizCard";

export default function ParentDashboard({ user }) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", date_of_birth: "", gender: "male", relationship: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/api/parent/children");
      setChildren(data.children);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/api/parent/children", form);
      setChildren([data.child, ...children]);
      setForm({ name: "", date_of_birth: "", gender: "male", relationship: "", notes: "" });
      setShowAdd(false);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const remove = async (child_id, name) => {
    if (!window.confirm(`Remove ${name}? Existing reports will remain.`)) return;
    try {
      await api.delete(`/api/parent/children/${child_id}`);
      setChildren(children.filter(c => c.child_id !== child_id));
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader
        user={user}
        subtitle="Track your children's growth, generate personalized nutrition plans, and share with your doctor."
      />

      <Section
        title="My children"
        description="Add a profile so future analyses auto-populate the child's age and gender."
        testid="parent-children-section"
        right={
          <button onClick={() => setShowAdd(!showAdd)}
            data-testid="parent-add-child-button"
            className="text-sm px-3 py-1.5 rounded bg-sky-500 hover:bg-sky-400 text-white">
            {showAdd ? "Cancel" : "+ Add child"}
          </button>
        }
      >
        {showAdd && (
          <form onSubmit={add} className="mb-4 p-4 rounded-xl bg-[#0E1A3A] border border-slate-800 space-y-3" data-testid="parent-add-child-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required placeholder="Name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="px-3 py-2 rounded bg-[#0B1428] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none"
                data-testid="child-name-input" />
              <input required type="date" value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                className="px-3 py-2 rounded bg-[#0B1428] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none"
                data-testid="child-dob-input" />
              <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="px-3 py-2 rounded bg-[#0B1428] border border-slate-700 text-slate-100 outline-none"
                data-testid="child-gender-select">
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <input placeholder="Relationship (e.g. Son, Daughter)" value={form.relationship}
                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                className="px-3 py-2 rounded bg-[#0B1428] border border-slate-700 text-slate-100 outline-none" />
            </div>
            {error && <p className="text-sm text-rose-300">{error}</p>}
            <button type="submit" disabled={submitting}
              data-testid="child-add-submit"
              className="px-4 py-1.5 rounded bg-sky-500 hover:bg-sky-400 text-white text-sm disabled:opacity-60">
              {submitting ? "Adding…" : "Add child"}
            </button>
          </form>
        )}
        {loading ? (
          <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-6 text-sm text-slate-400">Loading…</div>
        ) : children.length === 0 ? (
          <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-6 text-sm text-slate-400" data-testid="parent-no-children">
            No children added yet. Click <strong>Add child</strong> above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {children.map(c => (
              <div key={c.child_id} className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-4 flex items-start justify-between gap-3" data-testid={`child-card-${c.child_id}`}>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-slate-100">{c.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    DOB {c.date_of_birth} · {c.gender === "male" ? "♂ Male" : "♀ Female"}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    {c.report_count} report{c.report_count === 1 ? "" : "s"}
                  </p>
                </div>
                <button onClick={() => remove(c.child_id, c.name)}
                  data-testid={`child-remove-${c.child_id}`}
                  className="text-xs px-2 py-1 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border border-rose-500/30">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      <ToolTiles />

      <ParentQuizCard />

      <Section title="My reports" description="Every BoneAH & NutriGuide analysis you run is saved here." testid="parent-reports-section">
        <ReportsPanel userRole={user.role} />
      </Section>
    </>
  );
}
